import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';

interface LeadConversionModalProps {
  open: boolean;
  defaultName: string;
  defaultPhone: string;
  onClose: () => void;
  onConvert: (payload: { name: string; phone: string }) => Promise<void> | void;
  isSubmitting?: boolean;
  error?: string | null;
}

export function LeadConversionModal({
  open,
  defaultName,
  defaultPhone,
  onClose,
  onConvert,
  isSubmitting,
  error,
}: LeadConversionModalProps) {
  const [name, setName] = useState(defaultName);
  const [phone, setPhone] = useState(defaultPhone);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setPhone(defaultPhone);
      setLocalError(null);
    }
  }, [open, defaultName, defaultPhone]);

  if (!open) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setLocalError('Name is required');
      return;
    }
    if (!phone.trim()) {
      setLocalError('Phone is required');
      return;
    }
    setLocalError(null);
    try {
      await onConvert({ name: name.trim(), phone: phone.trim() });
    } catch (err) {
      setLocalError(
        err instanceof Error ? err.message : 'Failed to convert lead to client'
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-[#212121]">
              Convert lead to client
            </h3>
            <p className="text-sm text-gray-500">
              Confirm the contact details before saving them as a client.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        {(localError || error) && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2 rounded-xl">
            {localError || error}
          </div>
        )}
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87]/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87]/20 outline-none"
            />
          </div>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-xl text-sm font-semibold text-white ${
                isSubmitting
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-[#5B8E87] hover:bg-[#4F7A74]'
              }`}
            >
              {isSubmitting ? 'Converting…' : 'Save as Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
